import { createAction } from '@reduxjs/toolkit';

/**
 * Channel tag for routing WS-send actions to the MM connection. Set on a
 * dispatched action's `meta.ws` so the app's WS request middleware forwards the
 * payload through the MM channel.
 */
export const wsMmChannel = 'MM' as const;

export interface WsActionMeta extends Record<string, unknown> {}

/**
 * Creates a Redux action tagged for the MM WS channel. The dashboard's WS
 * request middleware reads `meta.ws` and forwards the payload over the MM
 * connection. Reusable across data packages that send MM commands.
 */
export function createMmWsAction(
  type: string,
  callback: () => { command: string },
): ReturnType<
  typeof createAction<
    (meta?: WsActionMeta) => { payload: ReturnType<typeof callback>; meta: WsActionMeta },
    string
  >
>;

export function createMmWsAction<T, P>(
  type: string,
  callback: (data: T) => { command: string } & P,
): ReturnType<
  typeof createAction<
    (data: T, meta?: WsActionMeta) => { payload: ReturnType<typeof callback>; meta: WsActionMeta },
    string
  >
>;

export function createMmWsAction<T>(
  type: string,
  command: string,
): ReturnType<
  typeof createAction<
    (data: T, meta?: WsActionMeta) => { payload: T & { command: string }; meta: WsActionMeta },
    string
  >
>;

export function createMmWsAction<T = undefined, P = undefined>(
  type: string,
  callback: string | (() => { command: string }) | ((data?: T) => { command: string } & P),
) {
  return createAction(type, (data?: T, meta?: WsActionMeta) => ({
    payload:
      typeof callback === 'function'
        ? callback(data)
        : {
            command: callback,
            ...(data && { ...data }),
          },
    meta: {
      ...meta,
      ws: wsMmChannel,
    },
  }));
}
